'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { HeroBanner } from '@/components/frontend/common/hero-banner';

export function PrivacyPolicyPageContent() {
  const t = useTranslations('privacyPolicyPage');

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <HeroBanner
        page="privacy"
        title={t('title')}
        description={t('description')}
        pill={t('pill')}
        className="border-b border-gray-200 dark:border-gray-900"
      />

      <SectionWrapper variant="default" padding="md" className="relative overflow-hidden">
        {/* Decorative background */}
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        <div className="relative z-10 container">

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              {t('welcomeTitle')}
            </h1>

            <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">
              {t('intro')}
            </p>

            <p className="text-gray-700 dark:text-gray-300 mb-4">
              <strong>{t('companyName')}</strong> {t('companyAddress')}
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-8">
              {t('companyFullAddress')}
            </p>

            <p className="text-gray-700 dark:text-gray-300 mb-8">
              {t('policyApplies')}
            </p>

            {/* Website Visitors */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('websiteVisitors.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('websiteVisitors.description')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('websiteVisitors.ipAddresses')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {t('websiteVisitors.disclosure')}
              </p>
            </section>

            {/* Gathering of Personally-Identifying Information */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('gatheringInfo.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('gatheringInfo.description')}
              </p>
            </section>

            {/* Security */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('security.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('security.description')}
              </p>
            </section>

            {/* Advertisements */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('advertisements.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('advertisements.description')}
              </p>
            </section>

            {/* Links To External Sites */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('externalLinks.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('externalLinks.description')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {t('externalLinks.noControl')}
              </p>
            </section>

            {/* Protection of Certain Personally-Identifying Information */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('protection.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('protection.description')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('protection.emailUpdates')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('protection.requestPublish')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {t('protection.measures')}
              </p>
            </section>

            {/* Aggregated Statistics */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('aggregatedStats.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('aggregatedStats.description')}
              </p>
            </section>

            {/* Affiliate Disclosure */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('affiliateDisclosure.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('affiliateDisclosure.description')}
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('cookies.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('cookies.description')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('cookies.definition')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('cookies.usage')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('cookies.refuse')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {t('cookies.acknowledge')}
              </p>
            </section>

            {/* E-commerce */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('ecommerce.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('ecommerce.description')}
              </p>
            </section>

            {/* Business Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('businessTransfers.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('businessTransfers.description')}
              </p>
            </section>

            {/* Privacy Policy Changes */}
            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('changes.title')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('changes.description')}
              </p>
            </section>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
}

